import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import _ from 'lodash';

import { useTranslation } from 'react-i18next';
import { MAX_DAY, MIN_DAY } from '@/schedule/schedule.constants';
import { DayOfTheWeek, MonthlyScheduleTypeName } from '@/schedule/schedule.types';
import { Checkbox, Select, TextField } from '@seeqdev/qomponents';
import type { MonthlyScheduleData } from '@/schedule/schedule.types';

export type Option = { value: string; label: string };

interface MonthlyScheduleProps {
  monthlySchedule: MonthlyScheduleData;
  setMonthlySchedule: (monthlyScheduleData: MonthlyScheduleData) => void;
  disabled?: boolean;
}

const NTH_OPTIONS = [
  {
    value: 1,
    label: 'SCHEDULE_TYPE.MONTHLY.NTH.FIRST',
  },
  {
    value: 2,
    label: 'SCHEDULE_TYPE.MONTHLY.NTH.SECOND',
  },
  {
    value: 3,
    label: 'SCHEDULE_TYPE.MONTHLY.NTH.THIRD',
  },
  {
    value: 4,
    label: 'SCHEDULE_TYPE.MONTHLY.NTH.FOURTH',
  },
];

const BY_DAY_OF_MONTH_ID = 'monthly-schedule-config__by-day-of-month';
const BY_DAY_OF_WEEK_ID = 'monthly-schedule-config__by-day-of-week';

export const MonthlySchedule = ({ monthlySchedule, setMonthlySchedule, disabled = false }: MonthlyScheduleProps) => {
  const { selectedType } = monthlySchedule;
  const dayOfMonthData = monthlySchedule.data[MonthlyScheduleTypeName.BY_DAY_OF_MONTH];
  const dayOfWeekData = monthlySchedule.data[MonthlyScheduleTypeName.BY_DAY_OF_WEEK];

  const { t } = useTranslation();

  const isByDayOfMonth = selectedType === MonthlyScheduleTypeName.BY_DAY_OF_MONTH;
  const isByDayOfWeek = selectedType === MonthlyScheduleTypeName.BY_DAY_OF_WEEK;
  const dayOfTheWeekOptions = _.map(NTH_OPTIONS, ({ value, label }) => ({ value, label: t(label) }));
  const daySelectOptions = _.map(Object.keys(DayOfTheWeek), (day) => ({
    value: day,
    label: t(`SCHEDULE_TYPE.WEEKLY.${day}`),
  }));

  const selectType = (selectedType: MonthlyScheduleTypeName) => {
    setMonthlySchedule(_.extend({}, monthlySchedule, { selectedType }));
  };

  const updateDayOfMonthDays = (event: React.ChangeEvent<HTMLInputElement>) => {
    const day = _.clamp(_.toInteger(event.target.value), MIN_DAY, MAX_DAY);
    const nextMonthlySchedule = { ...monthlySchedule };
    nextMonthlySchedule.data[MonthlyScheduleTypeName.BY_DAY_OF_MONTH].day = day;
    setMonthlySchedule(nextMonthlySchedule);
  };

  const selectNthDayOfWeek = (option: Option) => {
    const nextMonthlySchedule = { ...monthlySchedule };
    nextMonthlySchedule.data[MonthlyScheduleTypeName.BY_DAY_OF_WEEK].nth = _.toInteger(option.value);
    setMonthlySchedule(nextMonthlySchedule);
  };

  const selectDayOfWeek = (option: Option) => {
    const nextMonthlySchedule = { ...monthlySchedule };
    nextMonthlySchedule.data[MonthlyScheduleTypeName.BY_DAY_OF_WEEK].dayOfWeek = option.value as DayOfTheWeek;
    setMonthlySchedule(nextMonthlySchedule);
  };

  return (
    <div
      data-testid="monthly-schedule-config"
      className={clsx('flexRowContainer', stylex.props(styles.monthlySchedule).className)}>
      <div className={clsx('monthly-schedule-row flexColumnContainer', stylex.props(styles.calendarDate).className)}>
        <Checkbox
          id={BY_DAY_OF_MONTH_ID}
          testId={BY_DAY_OF_MONTH_ID}
          type="radio"
          xstyle={styles.typeChoice}
          label={t('SCHEDULE_TYPE.MONTHLY.ON_DAY')}
          checked={isByDayOfMonth}
          disabled={disabled}
          onChange={() => !isByDayOfMonth && selectType(MonthlyScheduleTypeName.BY_DAY_OF_MONTH)}
        />
        <TextField
          testId="monthly-schedule-config__by-day-of-month__day"
          type="number"
          required={true}
          max={MAX_DAY}
          min={MIN_DAY}
          showError={_.isNaN(dayOfMonthData.day) || dayOfMonthData.day < MIN_DAY || dayOfMonthData.day > MAX_DAY}
          extraClassNames="noValidationIcons"
          xstyle={styles.monthDay}
          value={dayOfMonthData.day.toString()}
          disabled={disabled}
          onChange={updateDayOfMonthDays}
        />
      </div>
      <div
        className={clsx(
          'monthly-schedule-row monthly-schedule-row-by-day-of-week flexColumnContainer',
          stylex.props(styles.weekday).className,
        )}>
        <Checkbox
          id={BY_DAY_OF_WEEK_ID}
          testId={BY_DAY_OF_WEEK_ID}
          type="radio"
          xstyle={styles.typeChoice}
          label={t('SCHEDULE_TYPE.MONTHLY.ON_THE')}
          checked={isByDayOfWeek}
          disabled={disabled}
          onChange={() => !isByDayOfWeek && selectType(MonthlyScheduleTypeName.BY_DAY_OF_WEEK)}
        />
        <span data-testid="monthly-schedule-config__by-day-of-week__nth">
          <Select
            extraClassNames="spec_monthly-schedule-config__by-day-of-week__nth"
            xstyle={styles.weekOrdinal}
            value={_.find(dayOfTheWeekOptions, { value: dayOfWeekData.nth })}
            options={dayOfTheWeekOptions}
            isDisabled={disabled}
            onChange={selectNthDayOfWeek}
          />
        </span>
        <span data-testid="monthly-schedule-config__by-day-of-week__day">
          <Select
            extraClassNames="spec-monthly-schedule-config__by-day-of-week__day"
            xstyle={styles.weekDay}
            value={_.find(daySelectOptions, { value: dayOfWeekData.dayOfWeek })}
            options={daySelectOptions}
            isDisabled={disabled}
            onChange={selectDayOfWeek}
          />
        </span>
      </div>
    </div>
  );
};

const styles = stylex.create({
  typeChoice: { marginTop: 5, marginBottom: 5, marginRight: 5 },
  monthDay: { width: 55 },
  weekOrdinal: { width: 90, marginRight: 5 },
  weekDay: { width: 110 },
  monthlySchedule: {
    marginBottom: '0rem',
  },
  calendarDate: {
    marginBottom: '0.9375rem',
    flex: 'none',
  },
  weekday: {
    marginBottom: '0.9375rem',
    flex: 'none',
  },
});
